import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  ProviderContactFields,
  ProviderIdentityFields,
  SendInvitationField,
} from "./invite-provider-fields";
import { RosterProviderMatchStatus } from "@api/shared-runtime/roster-provider-match";
import { NameMatchPanel } from "./roster-match/name-match-panel";
import { useInviteProviderForm } from "./use-invite-provider-form";
import { ProviderEmploymentField } from "./provider-employment-field";

type InviteProviderDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (organizationBusinessUnitId: string) => void | Promise<void>;
  onViewProvider?: (affiliationId: string) => void;
  variant?: "invitations" | "workspace";
};

export function InviteProviderDialog({
  open,
  onOpenChange,
  onSaved,
  onViewProvider,
  variant = "invitations",
}: InviteProviderDialogProps) {
  const {
    form,
    manualEntry,
    setManualEntry,
    providerNpi,
    addProvider,
    identityConflict,
    nameMatches,
    isCheckingMatches,
    reinstateProvider,
    reinstateEndedProvider,
    submit,
    employmentStatus,
    employmentError,
    chooseEmploymentStatus,
  } = useInviteProviderForm({
    open,
    onOpenChange,
    onSaved,
    requireEmploymentStatus: variant === "workspace",
  });

  const isBusy =
    addProvider.isPending || reinstateProvider.isPending || isCheckingMatches;
  const reinstatableId =
    identityConflict?.status === RosterProviderMatchStatus.Ended
      ? identityConflict.endedAffiliationId
      : null;
  const viewableId =
    identityConflict?.status === RosterProviderMatchStatus.Active
      ? identityConflict.affiliationId
      : null;

  const submitLabel =
    nameMatches.length > 0 ? "Add as a new provider" : "Add provider";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isBusy) {
          onOpenChange(next);
        }
      }}
    >
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add provider</DialogTitle>
          <DialogDescription>
            Add a provider to this business unit's roster. You can manage their
            record without creating a login or sending an invitation.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="space-y-4">
            {variant === "invitations" && <ProviderContactFields form={form} />}
            <ProviderIdentityFields
              form={form}
              manualEntry={manualEntry}
              providerNpi={providerNpi}
              onManualEntryChange={setManualEntry}
              afterNpi={
                variant === "workspace" ? (
                  <ProviderContactFields form={form} phoneFirst />
                ) : undefined
              }
            />
            {variant === "workspace" && (
              <ProviderEmploymentField
                value={employmentStatus}
                error={employmentError}
                onChange={chooseEmploymentStatus}
                disabled={addProvider.isPending}
              />
            )}
            <SendInvitationField form={form} />

            {/* Form-level errors have no field to render under — without this a
                failed submit would look like a dead button. */}
            {form.formState.errors.root?.message && (
              <p role="alert" className="text-sm font-medium text-destructive">
                {form.formState.errors.root.message}
              </p>
            )}
            {nameMatches.length > 0 && (
              <NameMatchPanel
                matches={nameMatches}
                disabled={isBusy}
                onViewProvider={onViewProvider}
                onReinstate={reinstateEndedProvider}
              />
            )}
            {reinstatableId && (
              <Button
                type="button"
                variant="outline"
                disabled={isBusy}
                onClick={() => reinstateEndedProvider(reinstatableId)}
              >
                {reinstateProvider.isPending
                  ? "Reinstating..."
                  : "Reinstate provider"}
              </Button>
            )}
            {viewableId && onViewProvider && (
              <Button
                type="button"
                variant="outline"
                disabled={isBusy}
                onClick={() => onViewProvider(viewableId)}
              >
                View provider
              </Button>
            )}

            <DialogFooter className="mt-2 border-t pt-4 gap-2 sm:space-x-0">
              <Button
                type="button"
                variant="outline"
                className="w-full lg:w-auto"
                disabled={isBusy}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="w-full lg:w-auto"
                disabled={isBusy}
              >
                {addProvider.isPending ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Adding...
                  </span>
                ) : (
                  submitLabel
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
